/**
 * Internals shared between `SearchableSelect` (single-select) and
 * `SearchableMultiSelect` (multi-select) — filtering, keyboard-navigation math,
 * and depth indentation. Extracted so the two controls cannot silently drift
 * apart; neither control's public API or behaviour changes because of this
 * file existing.
 */
export const DEFAULT_SEARCH_THRESHOLD = 5;
/**
 * Margin-start variant per depth. Logical `ms-*`, never `ml-*` (docs/011 RTL
 * rule). Tailwind needs whole class names to exist in the source, so this is a
 * lookup table and not an interpolated string. Capped at depth 6; deeper rows
 * reuse the depth-6 class rather than marching off the panel.
 */
export const DEPTH_INDENT = {
    0: "ms-0",
    1: "ms-4",
    2: "ms-8",
    3: "ms-12",
    4: "ms-16",
    5: "ms-20",
    6: "ms-24",
};
export const MAX_DEPTH_VARIANT = 6;
export const DEFAULT_INDENT = "ms-0";
export function indentFor(depth) {
    if (!depth || depth < 0)
        return DEFAULT_INDENT;
    return DEPTH_INDENT[Math.min(depth, MAX_DEPTH_VARIANT)] ?? DEFAULT_INDENT;
}
/**
 * Case- and accent-insensitive. "Muenster" will not find "Münster", but typing
 * either "Munster" or "Münster" will — which is the case a user actually hits
 * when their keyboard and the data disagree about diacritics.
 */
export function normalize(text) {
    return text
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLocaleLowerCase();
}
export function matches(option, needle) {
    if (normalize(option.label).includes(needle))
        return true;
    return (option.keywords ?? []).some((keyword) => normalize(keyword).includes(needle));
}
/**
 * Next selectable index in `list`, walking `direction` from `from` and wrapping.
 * Returns `-1` when every row is disabled — the caller must not assume a hit.
 */
export function nextEnabledIndex(list, from, direction) {
    const count = list.length;
    if (count === 0)
        return -1;
    for (let step = 1; step <= count; step += 1) {
        const index = (((from + direction * step) % count) + count) % count;
        if (!list[index]?.disabled)
            return index;
    }
    return -1;
}
export function firstEnabledIndex(list) {
    return list.findIndex((option) => !option.disabled);
}
//# sourceMappingURL=searchable-select-shared.js.map